import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Modal } from 'components/Modal';
import { ChildrenProp } from 'types/commons';

interface ModalContextInterface {
    showCloseButton?: boolean;
    showModal: (modalId: string, component: React.ReactNode, className?: string, showCloseButton?: boolean) => void;
    closeModal: () => void;
    modalOpened: boolean;
    modalData: ModalDataInterface | undefined;
    modalIsClosing: boolean;
}

export interface ModalDataInterface {
    component: React.ReactNode | undefined;
    id: string | undefined;
    className?: string;
    showCloseButton?: boolean;
}

const ModalContext = createContext({} as ModalContextInterface);

function ModalProviderData(): ModalContextInterface {
    const location = useLocation();
    const previousPathnameRef = useRef(location.pathname);

    const [modalData, setModalData] = useState<ModalDataInterface>();
    const [modalIsClosing, setModalIsClosing] = useState<boolean>(false);

    const showModal = useCallback((modalId: string, component: React.ReactNode, className?: string, showCloseButton?: boolean) => {
        setModalData({
            id: modalId,
            component,
            className,
            showCloseButton
        });
    }, []);

    const closeModal = useCallback(() => {
        setModalIsClosing(true);

        setTimeout(() => {
            setModalData({
                component: undefined,
                id: undefined
            });
            setModalIsClosing(false);
        }, 300);
    }, []);

    useEffect(() => {
        if (previousPathnameRef.current !== location.pathname) {
            // Só fecha se há modal aberto — evita enfileirar um setTimeout que
            // depois clobba um modal aberto por uma página que acabou de montar
            // (~300ms após a navegação).
            if (modalData?.id && modalData.id !== 'notification') {
                closeModal();
            }
            previousPathnameRef.current = location.pathname;
        }
    }, [closeModal, location.pathname, modalData?.id]);

    const modalOpened = useMemo(() => {
        return typeof modalData?.id === 'string' && !!modalData?.id.length;
    }, [modalData?.id]);

    useEffect(() => {
        if (modalOpened) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
    }, [modalOpened]);

    const escFunction = useCallback((event: { key: string }) => {
        if (event.key === 'Escape') {
            closeModal();
        }
    }, [closeModal]);

    useEffect(() => {
        const listener = async ({ data }: any) => {
            if (data.type === 'achievement') {
                setModalData({
                    component: '',
                    id: 'achievement'
                });
            }
        };

        document.addEventListener('keydown', escFunction, false);
        window.addEventListener('message', listener);

        return () => {
            window.removeEventListener('message', listener);
            document.removeEventListener('keydown', escFunction, false);
        };
    }, [escFunction]);

    return {
        showModal,
        closeModal,
        modalOpened,
        modalData,
        modalIsClosing
    };
}

const ModalProvider = ({ children }: ChildrenProp) => {
    const contextData: ModalContextInterface = ModalProviderData();
    return (
        <ModalContext.Provider value={contextData}>
            {children}
            {contextData.modalOpened && <Modal />}
        </ModalContext.Provider>
    );
};

const useModal = (): ModalContextInterface => {
    const context = useContext(ModalContext);

    if (typeof context === 'undefined') {
        throw new Error('useModal must be used within an ModalProvider');
    }

    return context;
};

export { ModalProvider, useModal };
